// Backend integration point: replace all exports with API calls to your backend service

export type StockStatus = 'in-stock' | 'low-stock' | 'out-of-stock';
export type UnitType = 'g' | 'kg' | 'ml' | 'L' | 'pcs' | 'oz' | 'lb';

/** A single packaging element (e.g. a jar, a label, a sealing sticker) */
export interface PackagingElement {
  id: string;
  name: string;           // e.g. "1L Glass Jar", "Sealing Sticker", "Front Label"
  unit: UnitType;
  costPerUnit: number;    // cost per single element
  stockQty: number;       // how many in stock
  reorderLevel: number;
  category: string;       // e.g. "Jars", "Labels", "Stickers", "Tags"
  supplier?: string;
  notes?: string;
  imageUrl?: string;
}

/** An item within a packaging group — references a PackagingElement */
export interface PackagingGroupItem {
  elementId: string;
  quantity: number;       // how many of this element per group (e.g. 1 jar, 2 labels)
}

/** A named packaging group combining multiple elements (e.g. "1L Jar Full Pack") */
export interface PackagingGroup {
  id: string;
  name: string;           // e.g. "1L Jar Full Pack", "100ml Bottle Basic"
  description?: string;
  items: PackagingGroupItem[];
  // totalCost is computed from items × element costs
  imageUrl?: string;
}

export interface Purchase {
  id: string;
  materialId: string;
  date: string;
  quantity: number;
  unitPrice: number;
  supplier: string;
  totalCost: number;
  notes?: string;
}

export interface RawMaterial {
  id: string;
  name: string;
  unit: UnitType;
  currentCostPerUnit: number;
  previousCostPerUnit: number;
  totalStock: number;
  usedStock: number;
  reorderLevel: number;
  supplier: string;
  lastPurchased: string;
  category: string;
  wasteQuantity: number;
  imageUrl?: string;
}

export interface RecipeIngredient {
  materialId: string;
  quantity: number;
}

/** A packaging material used in a specific packaging configuration */
export interface PackagingMaterial {
  materialId: string;
  quantityPerUnit: number; // how many of this material per finished unit (e.g. 1 bottle, 1 label, 1 lid)
}

/**
 * A packaging configuration defines how a full batch is packaged.
 * e.g. "2 × 250ml bottles" or "5 × 100ml bottles"
 * Each config has its own packaging materials so costs differ per config.
 */
export interface PackagingConfig {
  id: string;
  name: string;           // e.g. "2 × 250ml bottles", "5 × 100ml jars"
  unitsPerBatch: number;  // how many finished units come from one batch
  sellingPrice: number;   // selling price per finished unit
  packagingMaterials: PackagingMaterial[]; // packaging items (bottles, labels, lids, bags, etc.)
  loadedGroupId?: string;           // id of the packaging group loaded into this config
  loadedGroupCostPerUnit?: number;  // pre-calculated cost per unit from the loaded group
  /** Split packaging: multiple packaging group slots, each with a unit count.
   *  e.g. [{ groupId: 'pg-001', unitCount: 1 }, { groupId: 'pg-002', unitCount: 2 }]
   *  means 1 unit packed in group pg-001 and 2 units packed in group pg-002 from the same batch.
   */
  splitGroups?: SplitPackagingSlot[];
}

/** One slot in a split-packaging configuration */
export interface SplitPackagingSlot {
  id: string;           // local id for React key
  groupId: string;      // references PackagingGroup.id
  unitCount: number;    // how many finished units use this group
  volumePerUnit?: number; // volume/size per unit in this slot (e.g. 500 for 500ml, 250 for 250ml) — used for proportional ingredient cost
  sellingPrice?: number;  // selling price per unit for this specific slot size
}

export interface Product {
  id: string;
  name: string;
  description: string;
  sellingPrice: number;   // used only when no packagingConfigs defined
  ingredients: RecipeIngredient[];
  createdAt: string;
  updatedAt: string;
  category: string;
  batchSize: number;
  packagingConfigs?: PackagingConfig[];
  imageUrl?: string;
}

export interface ProductionRun {
  id: string;
  productId: string;
  packagingConfigId?: string;  // which packaging config was used for this run
  packagingConfigName?: string; // snapshot of config name at time of run
  unitsProduced: number;
  date: string;
  notes?: string;
  batchCost: number;
  costPerUnit: number;
}

export type StockAdjustmentReason = 'sold' | 'used' | 'discarded' | 'broken' | 'stolen' | 'other';

/** A single stock reduction event for a finished product */
export interface StockAdjustmentEntry {
  id: string;
  productId: string;
  packagingConfigId?: string;   // which packaging config (variant) was affected
  packagingConfigName?: string;
  quantity: number;             // units removed (positive number)
  reason: StockAdjustmentReason;
  date: string;
  notes?: string;
}

/** Tracks current on-hand stock for a finished product (per packaging config if applicable) */
export interface FinishedProductStock {
  productId: string;
  packagingConfigId?: string;   // undefined = no packaging configs on this product
  packagingConfigName?: string;
  unitsOnHand: number;          // current available stock
}

export const SUPPLIERS = [
  'Apex Raw Supplies Co.',
  'GreenLeaf Ingredients Ltd.',
  'Pacific Chemical Works',
  'Northfield Distributors',
  'Sunridge Wholesale',
  'Delta Commodity Group',
];

export const rawMaterials: RawMaterial[] = [
  {
    id: 'mat-001',
    name: 'Shea Butter',
    unit: 'kg',
    currentCostPerUnit: 12.5,
    previousCostPerUnit: 11.8,
    totalStock: 48.5,
    usedStock: 22.3,
    reorderLevel: 10,
    supplier: 'GreenLeaf Ingredients Ltd.',
    lastPurchased: '2026-08-18',
    category: 'Fats & Oils',
    wasteQuantity: 0.8,
  },
  {
    id: 'mat-002',
    name: 'Coconut Oil',
    unit: 'L',
    currentCostPerUnit: 4.2,
    previousCostPerUnit: 4.2,
    totalStock: 32.0,
    usedStock: 18.5,
    reorderLevel: 8,
    supplier: 'GreenLeaf Ingredients Ltd.',
    lastPurchased: '2026-08-15',
    category: 'Fats & Oils',
    wasteQuantity: 0.5,
  },
  {
    id: 'mat-003',
    name: 'Sodium Hydroxide (Lye)',
    unit: 'kg',
    currentCostPerUnit: 3.8,
    previousCostPerUnit: 3.5,
    totalStock: 7.2,
    usedStock: 5.6,
    reorderLevel: 5,
    supplier: 'Pacific Chemical Works',
    lastPurchased: '2026-08-10',
    category: 'Chemicals',
    wasteQuantity: 0.1,
  },
  {
    id: 'mat-004',
    name: 'Lavender Essential Oil',
    unit: 'ml',
    currentCostPerUnit: 0.18,
    previousCostPerUnit: 0.16,
    totalStock: 340,
    usedStock: 280,
    reorderLevel: 100,
    supplier: 'Apex Raw Supplies Co.',
    lastPurchased: '2026-08-20',
    category: 'Fragrance',
    wasteQuantity: 5,
  },
  {
    id: 'mat-005',
    name: 'Distilled Water',
    unit: 'L',
    currentCostPerUnit: 0.8,
    previousCostPerUnit: 0.8,
    totalStock: 85.0,
    usedStock: 42.0,
    reorderLevel: 20,
    supplier: 'Northfield Distributors',
    lastPurchased: '2026-08-12',
    category: 'Liquids',
    wasteQuantity: 1.2,
  },
  {
    id: 'mat-006',
    name: 'Beeswax Pellets',
    unit: 'kg',
    currentCostPerUnit: 18.0,
    previousCostPerUnit: 17.5,
    totalStock: 3.8,
    usedStock: 6.2,
    reorderLevel: 4,
    supplier: 'Sunridge Wholesale',
    lastPurchased: '2026-08-05',
    category: 'Waxes',
    wasteQuantity: 0.2,
  },
  {
    id: 'mat-007',
    name: 'Citric Acid',
    unit: 'kg',
    currentCostPerUnit: 5.6,
    previousCostPerUnit: 5.9,
    totalStock: 12.4,
    usedStock: 7.8,
    reorderLevel: 3,
    supplier: 'Pacific Chemical Works',
    lastPurchased: '2026-08-08',
    category: 'Chemicals',
    wasteQuantity: 0.3,
  },
  {
    id: 'mat-008',
    name: 'Vitamin E Oil',
    unit: 'ml',
    currentCostPerUnit: 0.45,
    previousCostPerUnit: 0.42,
    totalStock: 180,
    usedStock: 140,
    reorderLevel: 60,
    supplier: 'Apex Raw Supplies Co.',
    lastPurchased: '2026-08-19',
    category: 'Additives',
    wasteQuantity: 2,
  },
  {
    id: 'mat-009',
    name: 'Arrowroot Powder',
    unit: 'kg',
    currentCostPerUnit: 7.2,
    previousCostPerUnit: 7.2,
    totalStock: 18.6,
    usedStock: 9.4,
    reorderLevel: 5,
    supplier: 'GreenLeaf Ingredients Ltd.',
    lastPurchased: '2026-08-14',
    category: 'Powders',
    wasteQuantity: 0.4,
  },
  {
    id: 'mat-010',
    name: 'Peppermint Essential Oil',
    unit: 'ml',
    currentCostPerUnit: 0.22,
    previousCostPerUnit: 0.2,
    totalStock: 95,
    usedStock: 85,
    reorderLevel: 50,
    supplier: 'Apex Raw Supplies Co.',
    lastPurchased: '2026-08-21',
    category: 'Fragrance',
    wasteQuantity: 3,
  },
  {
    id: 'mat-011',
    name: 'Castor Oil',
    unit: 'L',
    currentCostPerUnit: 6.5,
    previousCostPerUnit: 6.8,
    totalStock: 9.5,
    usedStock: 4.2,
    reorderLevel: 4,
    supplier: 'Delta Commodity Group',
    lastPurchased: '2026-08-17',
    category: 'Fats & Oils',
    wasteQuantity: 0.3,
  },
  {
    id: 'mat-012',
    name: 'Kaolin Clay',
    unit: 'kg',
    currentCostPerUnit: 4.9,
    previousCostPerUnit: 4.9,
    totalStock: 2.1,
    usedStock: 3.9,
    reorderLevel: 3,
    supplier: 'Northfield Distributors',
    lastPurchased: '2026-07-28',
    category: 'Powders',
    wasteQuantity: 0.1,
  },
  {
    id: 'mat-013',
    name: '250ml Glass Bottle',
    unit: 'pcs',
    currentCostPerUnit: 1.2,
    previousCostPerUnit: 1.1,
    totalStock: 200,
    usedStock: 80,
    reorderLevel: 50,
    supplier: 'Northfield Distributors',
    lastPurchased: '2026-08-10',
    category: 'Packaging',
    wasteQuantity: 0,
  },
  {
    id: 'mat-014',
    name: '100ml Glass Bottle',
    unit: 'pcs',
    currentCostPerUnit: 0.75,
    previousCostPerUnit: 0.7,
    totalStock: 300,
    usedStock: 120,
    reorderLevel: 80,
    supplier: 'Northfield Distributors',
    lastPurchased: '2026-08-10',
    category: 'Packaging',
    wasteQuantity: 0,
  },
  {
    id: 'mat-015',
    name: 'Product Label',
    unit: 'pcs',
    currentCostPerUnit: 0.15,
    previousCostPerUnit: 0.14,
    totalStock: 1000,
    usedStock: 350,
    reorderLevel: 200,
    supplier: 'Apex Raw Supplies Co.',
    lastPurchased: '2026-08-15',
    category: 'Packaging',
    wasteQuantity: 0,
  },
  {
    id: 'mat-016',
    name: 'Bottle Cap / Lid',
    unit: 'pcs',
    currentCostPerUnit: 0.1,
    previousCostPerUnit: 0.1,
    totalStock: 500,
    usedStock: 200,
    reorderLevel: 100,
    supplier: 'Northfield Distributors',
    lastPurchased: '2026-08-10',
    category: 'Packaging',
    wasteQuantity: 0,
  },
];

export const products: Product[] = [
  {
    id: 'prod-001',
    name: 'Lavender Shea Soap Bar',
    description: 'Cold-process soap bar with lavender fragrance and shea butter',
    sellingPrice: 8.5,
    ingredients: [
      { materialId: 'mat-001', quantity: 0.12 },
      { materialId: 'mat-002', quantity: 0.08 },
      { materialId: 'mat-003', quantity: 0.035 },
      { materialId: 'mat-004', quantity: 4 },
      { materialId: 'mat-005', quantity: 0.09 },
      { materialId: 'mat-008', quantity: 2 },
    ],
    createdAt: '2026-06-01',
    updatedAt: '2026-08-18',
    category: 'Soap',
    batchSize: 1,
  },
  {
    id: 'prod-002',
    name: 'Peppermint Lip Balm',
    description: 'Nourishing lip balm with beeswax and peppermint oil',
    sellingPrice: 4.25,
    ingredients: [
      { materialId: 'mat-006', quantity: 0.015 },
      { materialId: 'mat-002', quantity: 0.01 },
      { materialId: 'mat-010', quantity: 3 },
      { materialId: 'mat-008', quantity: 1 },
    ],
    createdAt: '2026-06-15',
    updatedAt: '2026-08-20',
    category: 'Lip Care',
    batchSize: 1,
  },
  {
    id: 'prod-003',
    name: 'Detox Clay Face Mask',
    description: 'Deep-cleansing face mask with kaolin clay and citric acid',
    sellingPrice: 0,
    ingredients: [
      { materialId: 'mat-012', quantity: 0.02 },
      { materialId: 'mat-007', quantity: 0.005 },
      { materialId: 'mat-009', quantity: 0.015 },
      { materialId: 'mat-008', quantity: 3 },
    ],
    createdAt: '2026-07-01',
    updatedAt: '2026-08-15',
    category: 'Skincare',
    batchSize: 1,
    packagingConfigs: [
      {
        id: 'cfg-003-a',
        name: '2 × 250ml bottles',
        unitsPerBatch: 2,
        sellingPrice: 14.0,
        packagingMaterials: [
          { materialId: 'mat-013', quantityPerUnit: 1 },
          { materialId: 'mat-015', quantityPerUnit: 1 },
          { materialId: 'mat-016', quantityPerUnit: 1 },
        ],
      },
      {
        id: 'cfg-003-b',
        name: '5 × 100ml bottles',
        unitsPerBatch: 5,
        sellingPrice: 7.5,
        packagingMaterials: [
          { materialId: 'mat-014', quantityPerUnit: 1 },
          { materialId: 'mat-015', quantityPerUnit: 1 },
          { materialId: 'mat-016', quantityPerUnit: 1 },
        ],
      },
    ],
  },
  {
    id: 'prod-004',
    name: 'Moisturizing Body Butter',
    description: 'Rich body butter with shea, coconut oil, and vitamin E',
    sellingPrice: 15.0,
    ingredients: [
      { materialId: 'mat-001', quantity: 0.08 },
      { materialId: 'mat-002', quantity: 0.04 },
      { materialId: 'mat-011', quantity: 0.02 },
      { materialId: 'mat-008', quantity: 5 },
      { materialId: 'mat-004', quantity: 6 },
    ],
    createdAt: '2026-07-10',
    updatedAt: '2026-08-22',
    category: 'Body Care',
    batchSize: 1,
  },
  {
    id: 'prod-005',
    name: 'Citrus Bath Bombs',
    description: 'Fizzy bath bomb with citric acid and arrowroot powder',
    sellingPrice: 5.5,
    ingredients: [
      { materialId: 'mat-007', quantity: 0.04 },
      { materialId: 'mat-009', quantity: 0.03 },
      { materialId: 'mat-005', quantity: 0.01 },
    ],
    createdAt: '2026-07-20',
    updatedAt: '2026-08-10',
    category: 'Bath',
    batchSize: 1,
  },
];

export const purchases: Purchase[] = [
  {
    id: 'pur-001',
    materialId: 'mat-001',
    date: '2026-08-18',
    quantity: 20,
    unitPrice: 12.5,
    supplier: 'GreenLeaf Ingredients Ltd.',
    totalCost: 250.0,
  },
  {
    id: 'pur-002',
    materialId: 'mat-001',
    date: '2026-07-10',
    quantity: 15,
    unitPrice: 11.8,
    supplier: 'GreenLeaf Ingredients Ltd.',
    totalCost: 177.0,
  },
  {
    id: 'pur-003',
    materialId: 'mat-002',
    date: '2026-08-15',
    quantity: 25,
    unitPrice: 4.2,
    supplier: 'GreenLeaf Ingredients Ltd.',
    totalCost: 105.0,
  },
  {
    id: 'pur-004',
    materialId: 'mat-003',
    date: '2026-08-10',
    quantity: 5,
    unitPrice: 3.8,
    supplier: 'Pacific Chemical Works',
    totalCost: 19.0,
  },
  {
    id: 'pur-005',
    materialId: 'mat-004',
    date: '2026-08-20',
    quantity: 200,
    unitPrice: 0.18,
    supplier: 'Apex Raw Supplies Co.',
    totalCost: 36.0,
  },
  {
    id: 'pur-006',
    materialId: 'mat-006',
    date: '2026-08-05',
    quantity: 5,
    unitPrice: 18.0,
    supplier: 'Sunridge Wholesale',
    totalCost: 90.0,
  },
  {
    id: 'pur-007',
    materialId: 'mat-010',
    date: '2026-08-21',
    quantity: 100,
    unitPrice: 0.22,
    supplier: 'Apex Raw Supplies Co.',
    totalCost: 22.0,
  },
  {
    id: 'pur-008',
    materialId: 'mat-008',
    date: '2026-08-19',
    quantity: 150,
    unitPrice: 0.45,
    supplier: 'Apex Raw Supplies Co.',
    totalCost: 67.5,
  },
  {
    id: 'pur-009',
    materialId: 'mat-005',
    date: '2026-08-12',
    quantity: 50,
    unitPrice: 0.8,
    supplier: 'Northfield Distributors',
    totalCost: 40.0,
  },
  {
    id: 'pur-010',
    materialId: 'mat-011',
    date: '2026-08-17',
    quantity: 10,
    unitPrice: 6.5,
    supplier: 'Delta Commodity Group',
    totalCost: 65.0,
  },
];

export const productionRuns: ProductionRun[] = [
  {
    id: 'run-001',
    productId: 'prod-001',
    unitsProduced: 24,
    date: '2026-08-22',
    notes: 'Morning batch',
    batchCost: 52.8,
    costPerUnit: 2.2,
  },
  {
    id: 'run-002',
    productId: 'prod-004',
    unitsProduced: 12,
    date: '2026-08-21',
    batchCost: 48.0,
    costPerUnit: 4.0,
  },
  {
    id: 'run-003',
    productId: 'prod-002',
    unitsProduced: 50,
    date: '2026-08-20',
    batchCost: 32.5,
    costPerUnit: 0.65,
  },
  {
    id: 'run-004',
    productId: 'prod-005',
    unitsProduced: 30,
    date: '2026-08-19',
    batchCost: 24.0,
    costPerUnit: 0.8,
  },
  {
    id: 'run-005',
    productId: 'prod-003',
    packagingConfigId: 'cfg-003-a',
    packagingConfigName: '2 × 250ml bottles',
    unitsProduced: 2,
    date: '2026-08-18',
    batchCost: 31.5,
    costPerUnit: 15.75,
  },
];

/** Initial finished product stock — derived from production runs minus any adjustments */
export const finishedProductStock: FinishedProductStock[] = [
  { productId: 'prod-001', unitsOnHand: 18 },
  { productId: 'prod-002', unitsOnHand: 35 },
  { productId: 'prod-003', packagingConfigId: 'cfg-003-a', packagingConfigName: '2 × 250ml bottles', unitsOnHand: 2 },
  { productId: 'prod-004', unitsOnHand: 8 },
  { productId: 'prod-005', unitsOnHand: 22 },
];

/** Initial stock adjustment history */
export const stockAdjustments: StockAdjustmentEntry[] = [
  {
    id: 'adj-001',
    productId: 'prod-001',
    quantity: 6,
    reason: 'sold',
    date: '2026-08-22',
    notes: 'Sold at farmers market',
  },
  {
    id: 'adj-002',
    productId: 'prod-002',
    quantity: 15,
    reason: 'sold',
    date: '2026-08-21',
    notes: 'Online order batch',
  },
  {
    id: 'adj-003',
    productId: 'prod-001',
    quantity: 2,
    reason: 'discarded',
    date: '2026-08-20',
    notes: 'Damaged packaging',
  },
];

export const packagingElements: PackagingElement[] = [
  {
    id: 'pe-001',
    name: '1L Glass Jar',
    unit: 'pcs',
    costPerUnit: 1.85,
    stockQty: 150,
    reorderLevel: 30,
    category: 'Jars',
    supplier: 'Northfield Distributors',
    notes: 'Wide-mouth 1 litre glass jar with lid',
  },
  {
    id: 'pe-002',
    name: '250ml Glass Jar',
    unit: 'pcs',
    costPerUnit: 0.95,
    stockQty: 300,
    reorderLevel: 60,
    category: 'Jars',
    supplier: 'Northfield Distributors',
  },
  {
    id: 'pe-003',
    name: '100ml Amber Bottle',
    unit: 'pcs',
    costPerUnit: 0.65,
    stockQty: 400,
    reorderLevel: 80,
    category: 'Bottles',
    supplier: 'Northfield Distributors',
  },
  {
    id: 'pe-004',
    name: 'Sealing Sticker',
    unit: 'pcs',
    costPerUnit: 0.08,
    stockQty: 2000,
    reorderLevel: 400,
    category: 'Stickers',
    supplier: 'Apex Raw Supplies Co.',
    notes: 'Tamper-evident sealing sticker',
  },
  {
    id: 'pe-005',
    name: 'Front Label',
    unit: 'pcs',
    costPerUnit: 0.18,
    stockQty: 1500,
    reorderLevel: 300,
    category: 'Labels',
    supplier: 'Apex Raw Supplies Co.',
  },
  {
    id: 'pe-006',
    name: 'Ingredient List Label',
    unit: 'pcs',
    costPerUnit: 0.12,
    stockQty: 1500,
    reorderLevel: 300,
    category: 'Labels',
    supplier: 'Apex Raw Supplies Co.',
  },
  {
    id: 'pe-007',
    name: 'Hang Tag',
    unit: 'pcs',
    costPerUnit: 0.22,
    stockQty: 800,
    reorderLevel: 150,
    category: 'Tags',
    supplier: 'Apex Raw Supplies Co.',
  },
  {
    id: 'pe-008',
    name: 'Kraft Paper Bag (Small)',
    unit: 'pcs',
    costPerUnit: 0.35,
    stockQty: 500,
    reorderLevel: 100,
    category: 'Bags',
    supplier: 'Sunridge Wholesale',
  },
  {
    id: 'pe-009',
    name: 'Lid / Cap',
    unit: 'pcs',
    costPerUnit: 0.12,
    stockQty: 600,
    reorderLevel: 100,
    category: 'Lids',
    supplier: 'Northfield Distributors',
  },
  {
    id: 'pe-010',
    name: 'Shrink Wrap Band',
    unit: 'pcs',
    costPerUnit: 0.05,
    stockQty: 3000,
    reorderLevel: 500,
    category: 'Sealing',
    supplier: 'Delta Commodity Group',
  },
];

export const packagingGroups: PackagingGroup[] = [
  {
    id: 'pg-001',
    name: '1L Jar Full Pack',
    description: '1L jar with sealing sticker, front label, ingredient list, and hang tag',
    items: [
      { elementId: 'pe-001', quantity: 1 },
      { elementId: 'pe-004', quantity: 1 },
      { elementId: 'pe-005', quantity: 1 },
      { elementId: 'pe-006', quantity: 1 },
      { elementId: 'pe-007', quantity: 1 },
    ],
  },
  {
    id: 'pg-002',
    name: '1L Jar Basic Pack',
    description: '1L jar with sealing sticker and front label only',
    items: [
      { elementId: 'pe-001', quantity: 1 },
      { elementId: 'pe-004', quantity: 1 },
      { elementId: 'pe-005', quantity: 1 },
    ],
  },
  {
    id: 'pg-003',
    name: '100ml Bottle Standard',
    description: '100ml amber bottle with lid, front label, and ingredient list',
    items: [
      { elementId: 'pe-003', quantity: 1 },
      { elementId: 'pe-009', quantity: 1 },
      { elementId: 'pe-005', quantity: 1 },
      { elementId: 'pe-006', quantity: 1 },
    ],
  },
  {
    id: 'pg-004',
    name: '250ml Jar Gift Pack',
    description: '250ml jar with sealing sticker, front label, ingredient list, hang tag, and kraft bag',
    items: [
      { elementId: 'pe-002', quantity: 1 },
      { elementId: 'pe-004', quantity: 1 },
      { elementId: 'pe-005', quantity: 1 },
      { elementId: 'pe-006', quantity: 1 },
      { elementId: 'pe-007', quantity: 1 },
      { elementId: 'pe-008', quantity: 1 },
    ],
  },
];

export const spendTrend = [
  { week: 'Jun 2', spend: 312 },
  { week: 'Jun 9', spend: 185 },
  { week: 'Jun 16', spend: 428 },
  { week: 'Jun 23', spend: 267 },
  { week: 'Jun 30', spend: 390 },
  { week: 'Jul 7', spend: 215 },
  { week: 'Jul 14', spend: 502 },
  { week: 'Jul 21', spend: 344 },
  { week: 'Jul 28', spend: 178 },
  { week: 'Aug 4', spend: 460 },
  { week: 'Aug 11', spend: 390 },
  { week: 'Aug 18', spend: 695 },
];

// Utility: calculate remaining stock
export function getRemainingStock(material: RawMaterial): number {
  return Math.max(0, material.totalStock - material.usedStock - material.wasteQuantity);
}

// Utility: get stock status
export function getStockStatus(material: RawMaterial): StockStatus {
  const remaining = getRemainingStock(material);
  if (remaining <= 0) return 'out-of-stock';
  if (remaining <= material.reorderLevel) return 'low-stock';
  return 'in-stock';
}

// Utility: calculate batch ingredient cost (ingredients only, no packaging)
export function calculateBatchIngredientCost(product: Product, materials: RawMaterial[]): number {
  return product.ingredients.reduce((total, ing) => {
    const mat = materials.find((m) => m.id === ing.materialId);
    if (!mat) return total;
    return total + ing.quantity * mat.currentCostPerUnit;
  }, 0);
}

// Utility: calculate packaging cost for a specific config (total for the batch)
export function calculatePackagingCostForConfig(config: PackagingConfig, materials: RawMaterial[]): number {
  const rawMatCost = config.packagingMaterials.reduce((total, pm) => {
    const mat = materials.find((m) => m.id === pm.materialId);
    if (!mat) return total;
    // quantityPerUnit × unitsPerBatch = total packaging material needed for the batch
    return total + pm.quantityPerUnit * config.unitsPerBatch * mat.currentCostPerUnit;
  }, 0);

  // If raw material mapping yielded no cost but a group was loaded, use the group's stored cost
  if (rawMatCost === 0 && config.loadedGroupCostPerUnit && config.loadedGroupCostPerUnit > 0) {
    return config.loadedGroupCostPerUnit * config.unitsPerBatch;
  }

  return rawMatCost;
}

// Utility: calculate total cost per unit for a packaging config
// = (batch ingredient cost + total packaging cost for config) / units per batch
export function calculateCostPerUnit(
  product: Product,
  config: PackagingConfig,
  materials: RawMaterial[]
): number {
  if (config.unitsPerBatch <= 0) return 0;
  const ingredientCost = calculateBatchIngredientCost(product, materials);
  const packagingCost = calculatePackagingCostForConfig(config, materials);
  return (ingredientCost + packagingCost) / config.unitsPerBatch;
}

// Utility: calculate product cost (for backward compat — returns batch ingredient cost)
export function calculateProductCost(product: Product, materials: RawMaterial[]): number {
  return calculateBatchIngredientCost(product, materials);
}

// Utility: calculate producible units for a product (based on ingredients only)
export function calculateProducibleUnits(product: Product, materials: RawMaterial[]): number {
  if (product.ingredients.length === 0) return 0;
  return Math.floor(
    Math.min(
      ...product.ingredients.map((ing) => {
        const mat = materials.find((m) => m.id === ing.materialId);
        if (!mat || ing.quantity === 0) return 0;
        return getRemainingStock(mat) / ing.quantity;
      })
    )
  );
}

// Utility: calculate total cost of a packaging group
export function calculatePackagingGroupCost(group: PackagingGroup, elements: PackagingElement[]): number {
  return group.items.reduce((total, item) => {
    const el = elements.find((e) => e.id === item.elementId);
    if (!el) return total;
    return total + item.quantity * el.costPerUnit;
  }, 0);
}

// Utility: calculate max producible units for a packaging group (limited by lowest stock element)
export function calculatePackagingGroupStock(group: PackagingGroup, elements: PackagingElement[]): number {
  if (group.items.length === 0) return 0;
  const limits = group.items.map((item) => {
    const el = elements.find((e) => e.id === item.elementId);
    if (!el || item.quantity === 0) return 0;
    return Math.floor(el.stockQty / item.quantity);
  });
  return Math.min(...limits);
}

// Utility: format currency
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

// Utility: calculate total inventory value
export function getTotalInventoryValue(materials: RawMaterial[]): number {
  return materials.reduce((sum, mat) => {
    return sum + getRemainingStock(mat) * mat.currentCostPerUnit;
  }, 0);
}